import { ARRAY_SEPARATOR, BOOLEAN_TRUE, EMPTY_VALUE, Query } from '@/services/api/request/query';

// if we wanted to format the visible url parameters differently, (for example, visible
// dates in urls could be iso dates instead of unix timestamps) these coded would have
// to be replaced
export {
  QueryValueEncoder,
  Query,
  boolean as booleanEncoder,
  nullableBoolean as nullableBooleanEncoder,
  string as stringEncoder,
  nullableString as nullableStringEncoder,
  number as numberEncoder,
  nullableNumber as nullableNumberEncoder,
  date as dateEncoder,
  nullableDate as nullableDateEncoder,
  numbers as numbersEncoder,
  strings as stringsEncoder,
  numericEnum as numericEnumEncoder,
  stringEnum as stringEnumEncoder,
  numericEnums as numericEnumsEncoder,
  stringEnums as stringEnumsEncoder,
} from '@/services/api/request/query';

export interface QueryValueDecoder<T> {
  (data: string): T;
}

export const stringDecoder: QueryValueDecoder<string> = i => i;
export const booleanDecoder: QueryValueDecoder<boolean> = (i: string): boolean => i === BOOLEAN_TRUE;
export const nullableBooleanDecoder: QueryValueDecoder<boolean | null> = (i: string): boolean | null => {
  return i === EMPTY_VALUE ? null : i === BOOLEAN_TRUE;
};
export const numberDecoder: QueryValueDecoder<number> = (i: string) => parseInt(i, 10);
export const nullableNumberDecoder: QueryValueDecoder<number | null> = (i: string): number | null => {
  return i === EMPTY_VALUE ? null : parseInt(i, 10);
};
export const dateDecoder: QueryValueDecoder<Date> = (i: string): Date => new Date(numberDecoder(i) * 1000);
export const nullableDateDecoder: QueryValueDecoder<Date | null> = (i: string): Date | null => {
  return i === EMPTY_VALUE ? null : dateDecoder(i);
};
export const stringsDecoder: QueryValueDecoder<string[]> = (i: string): string[] =>
  (i === EMPTY_VALUE ? [] : i.split(ARRAY_SEPARATOR));
export const numbersDecoder: QueryValueDecoder<number[]> = (i: string): number[] =>
  stringsDecoder(i).map(numberDecoder);
export const numericEnumDecoder = <T extends number>(set: T[], fallback: T): QueryValueDecoder<T> =>
  (i: string): T => {
    const parsed = numberDecoder(i) as T;
    return set.includes(parsed) ? parsed : fallback;
  };
export const stringEnumDecoder = <T extends string>(set: T[], fallback: T): QueryValueDecoder<T> =>
  (i: string): T => (set.includes(i as T) ? i as T : fallback);
export const numericEnumsDecoder = <T extends number>(values: T[]): QueryValueDecoder<T[]> => (i: string): T[] =>
  numbersDecoder(i).filter(v => values.includes(v as T)) as T[];
export const stringEnumsDecoder = <T extends string>(values: T[]): QueryValueDecoder<T[]> => (i: string): T[] =>
  stringsDecoder(i).filter(v => values.includes(v as T)) as T[];

export const filterEmptyQueryValues = (
  query: Query,
): Query => Object.keys(query).reduce(
  (carry, key) => {
    if (query[key] !== '') {
      carry[key] = query[key];
    }

    return carry;
  },
  {} as Query,
);
